import { HoverHintContent } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";

import type { ClampQcInspectionRow } from "./clampQcMockData";

export type InspectionTableRow = ClampQcInspectionRow & { clampSize?: "large" | "small" };

export function formatInspectionTimestamp(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function YesNo({ value }: { value: boolean | null }) {
  if (value === null) {
    return <span className="font-medium text-slate-400">—</span>;
  }
  return (
    <span className={cn("font-medium", value ? "text-emerald-700" : "text-slate-400")}>{value ? "Yes" : "No"}</span>
  );
}

function ResultBadge({ result }: { result: string }) {
  const passed = result.toLowerCase() === "passed";
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold",
        passed ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800",
      )}
    >
      {result}
    </span>
  );
}

function hookedToAnyTester(row: ClampQcInspectionRow): boolean {
  return row.testerA || row.testerB || row.testerC;
}

export function InspectionTable({
  rows,
  showClampSize = false,
  hideCartonColumn = false,
  emptyMessage = "No inspection lines match the current filters.",
}: {
  rows: InspectionTableRow[];
  showClampSize?: boolean;
  hideCartonColumn?: boolean;
  emptyMessage?: string;
}) {
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-slate-200">
      <table className="min-w-[920px] w-full border-collapse text-left text-xs text-slate-800">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-100 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            {showClampSize ? (
              <th className="whitespace-nowrap px-2 py-2">Clamp size</th>
            ) : null}
            {hideCartonColumn ? null : <th className="whitespace-nowrap px-2 py-2">Carton</th>}
            <th className="whitespace-nowrap px-2 py-2">Date and time</th>
            <th className="whitespace-nowrap px-2 py-2">Serial</th>
            <th className="whitespace-nowrap px-2 py-2">Unloaded line</th>
            <th className="whitespace-nowrap px-2 py-2">Loaded line before tester</th>
            <th className="whitespace-nowrap px-2 py-2 text-center">
              <HoverHintContent
                hoverHint="Yes if tester A, B, or C is hooked up (logical or)."
                className="inline-flex justify-center"
              >
                Hooked to any tester
              </HoverHintContent>
            </th>
            <th className="whitespace-nowrap px-2 py-2 text-center">Line harvest fail</th>
            <th className="whitespace-nowrap px-2 py-2">Inductance</th>
            <th className="whitespace-nowrap px-2 py-2">Inductance pass or fail</th>
            <th className="whitespace-nowrap px-2 py-2">Fail reason</th>
            <th className="whitespace-nowrap px-2 py-2">Observations</th>
            <th className="whitespace-nowrap px-2 py-2">Result</th>
            <th className="whitespace-nowrap px-2 py-2">Delta</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.clampSize ?? "x"}-${row.serial}-${index}`} className="border-b border-slate-100 odd:bg-white even:bg-slate-50/80">
              {showClampSize ? (
                <td className="whitespace-nowrap px-2 py-1.5 capitalize text-slate-600">
                  {row.clampSize ?? "—"}
                </td>
              ) : null}
              {hideCartonColumn ? null : (
                <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-slate-700">
                  {row.boxSerial <= 0 ? "—" : `#${row.boxSerial}`}
                </td>
              )}
              <td className="whitespace-nowrap px-2 py-1.5">{formatInspectionTimestamp(row.timestampIso)}</td>
              <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{row.serial}</td>
              <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{row.unloadedLine ?? "—"}</td>
              <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{row.loadedLineBeforeTester ?? "—"}</td>
              <td className="px-2 py-1.5 text-center">
                <YesNo value={hookedToAnyTester(row)} />
              </td>
              <td className="px-2 py-1.5 text-center">
                <YesNo value={row.lineHarvestFail} />
              </td>
              <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{row.inductance ?? "—"}</td>
              <td className="whitespace-nowrap px-2 py-1.5">{row.inductancePassFail?.trim() ? row.inductancePassFail : "—"}</td>
              <td className="max-w-[220px] break-words px-2 py-1.5 text-slate-600">{row.failReason ?? "—"}</td>
              <td className="max-w-[160px] px-2 py-1.5 text-slate-600">{row.observations ?? "—"}</td>
              <td className="whitespace-nowrap px-2 py-1.5">
                <ResultBadge result={row.result} />
              </td>
              <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{row.delta ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
